import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { applyDocumentLanguage } from '../config/i18n';
import { Language } from '../translations/types';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const isSupportedLanguage = (value: string | null | undefined): value is Language =>
  value === 'fr' || value === 'ar' || value === 'en';

/**
 * Default value used when `useLanguage()` is called outside <LanguageProvider>.
 *
 * - `t` delegates to the global i18next instance (populated synchronously at
 *   startup from `menuTranslations`), so keys still resolve to real strings;
 *   for a genuinely missing key i18next returns the key itself — the miss stays
 *   visible in the UI instead of being silently swallowed.
 * - `setLanguage` cannot update any state outside a provider: it is a
 *   documented no-op that warns in the console.
 */
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {
    console.warn('setLanguage() called outside <LanguageProvider> — ignored.');
  },
  t: (key: string): string => i18n.t(key) as string,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // useTranslation() subscribes this provider to i18next language changes, so
  // every consumer of the context re-renders with the new strings.
  const { t } = useTranslation();

  const [language, setLanguageState] = useState<Language>(() => {
    // Single storage key, shared with the i18next language detector ('language').
    const stored = localStorage.getItem('language');
    if (isSupportedLanguage(stored)) return stored;
    // No explicit choice yet: reuse what i18next resolved at init
    // (stored key -> navigator -> fallbackLng 'en') so state and i18next agree
    // from the first render instead of fighting over the language.
    const resolved = i18n.resolvedLanguage || i18n.language;
    return isSupportedLanguage(resolved) ? resolved : 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    localStorage.setItem('language', lang);
    // Update state and <html dir/lang> synchronously — RTL must flip immediately,
    // not after the (async) changeLanguage promise resolves.
    setLanguageState(lang);
    applyDocumentLanguage(lang);
    i18n.changeLanguage(lang).catch((error: unknown) => {
      console.error('Language change failed:', error);
    });
  }, []);

  // Keep i18next and <html lang/dir> in sync with the state. Runs on mount and
  // on every language change. (The previous version had `[]` deps while reading
  // `language` — a stale closure that only ever saw the initial value.)
  useEffect(() => {
    applyDocumentLanguage(language);
    if (i18n.resolvedLanguage !== language) {
      i18n.changeLanguage(language).catch((error: unknown) => {
        console.error('Language change failed:', error);
      });
    }
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
